import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../service";

interface ProvidersState {
  list: Record<string, any>[];
  count: number;
  activeItem: Record<string, any> | null;
  loading: boolean;
  activeItemloading: boolean;
  // The search `list` was fetched with. Differs from the page's applied filter
  // until that filter's fetch lands, so anything reasoning about what the list
  // on screen represents should read this, not the page state.
  listFilter: string;
  // Whether any list fetch has settled (succeeded or failed) yet, so the page
  // can tell a first load apart from an empty result.
  listLoaded: boolean;
  // Why the latest list fetch failed; null once one is in flight or succeeds.
  listError: string | null;
  // Only the most recent list request may write its result. Filters change
  // faster than responses come back, and an older one landing last would
  // otherwise overwrite the list with results for a term no longer applied.
  latestListRequestId: string | null;
}
interface GetProvidersListParams {
  page?: number;
  limit?: number;
  search?: string;
}
const initialState: ProvidersState = {
  list: [],
  count: 0,
  activeItem: {},
  loading: false,
  activeItemloading: false,
  listFilter: "",
  listLoaded: false,
  listError: null,
  latestListRequestId: null,
};

export const getProvidersList = createAsyncThunk(
  "providers/getProvidersList",
  async (
    { page, limit, search }: GetProvidersListParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await apiServices.systemManager.providers.getList(
        page || 1,
        limit || 10,
        search || "",
      );

      const { isError, data, pagination } = response.data;

      if (isError || !data || !Array.isArray(data)) {
        return rejectWithValue("Failed to fetch provider list");
      }

      return {
        list: data,
        count: pagination ? pagination.count : 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred");
    }
  },
);
export const getProviderDetailById = createAsyncThunk(
  "providers/getProviderDetailById",
  async (Id: string, { getState, rejectWithValue }) => {
    if (!Id) return rejectWithValue("Invalid Id ID");

    try {
      const response: any =
        await apiServices.systemManager.providers.getProvider(Id);
      const { isError, data } = response.data;
      if (isError || !data) {
        return rejectWithValue("Failed to fetch provider details");
      }

      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "An error occurred");
    }
  },
);

export const providersSlice = createSlice({
  name: "providers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getProvidersList.pending, (state, action) => {
        state.loading = true;
        state.listError = null;
        state.latestListRequestId = action.meta.requestId;
      })
      .addCase(getProvidersList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.loading = false;
        state.listLoaded = true;
        state.list = action.payload.list || [];
        state.count = action.payload.count || 0;
        state.listFilter = action.meta.arg.search || "";
      })
      .addCase(getProvidersList.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.loading = false;
        state.listLoaded = true;
        state.listError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch provider list";
        console.error("Error fetching providers details:", action.payload);
      })
      .addCase(getProviderDetailById.pending, (state) => {
        state.activeItemloading = true;
        state.activeItem = {};
      })
      .addCase(getProviderDetailById.fulfilled, (state, action) => {
        state.activeItemloading = false;
        state.activeItem = action.payload;
      })
      .addCase(getProviderDetailById.rejected, (state, action) => {
        state.activeItemloading = false;
        console.error("Error fetching  details:", action.payload);
      });
  },
});
export const {} = providersSlice.actions;
export default providersSlice.reducer;
